/**
 * Memory Graph: typed client over the local `api` helper (M5.2).
 *
 * Mirrors the M5.1 REST surface (every response is the `{data,error,meta}`
 * envelope). Shared DTOs come from `@tabbio/types`; do NOT use
 * `@tabbio/api-client` (dead), and do NOT import sigma/graphology here: this
 * module is shared between the web canvas and the native list, so it MUST stay
 * WebGL-free.
 *
 * The durable graph projection lives here; the legacy WorkingMemoryV1 blob
 * client stays in `agent-threads.ts` (the "Advanced" raw-JSON editor).
 */
import type {
  APIResponse,
  Episode,
  GraphEdge,
  GraphNode,
  GraphNodeType,
  MemoryAccessGrant,
  MemoryExport,
  MemoryGraphLayout,
  MemoryGraphMeta,
  MemoryRecallResult,
} from '@tabbio/types';
import { api } from './api';

// ── Error type ────────────────────────────────────────────────────────────────

export class MemoryGraphApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'MemoryGraphApiError';
  }
}

function unwrap<T>(res: APIResponse<T>): T {
  if (res.error) {
    throw new MemoryGraphApiError(
      res.error.message,
      res.error.code,
      res.meta as Record<string, unknown> | undefined,
    );
  }
  if (res.data == null) {
    throw new MemoryGraphApiError('Empty response', 'EMPTY_RESPONSE');
  }
  return res.data;
}

// ── Graph projection ────────────────────────────────────────────────────────

interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
  layout: MemoryGraphLayout;
}

export interface MemoryGraphResult extends GraphData {
  /** Envelope `meta`: plan cap / window / truncation flags. */
  meta: MemoryGraphMeta;
}

const DEFAULT_META: MemoryGraphMeta = {
  nodeCount: 0,
  edgeCount: 0,
  cap: null,
  windowDays: 30,
  truncated: false,
  extendedMemory: false,
};

export interface FetchMemoryGraphInput {
  /** Optional node-type filter (comma-joined into the query). */
  types?: GraphNodeType[];
  /** ISO date: only nodes created on/after this. */
  since?: string;
}

export async function fetchMemoryGraph(
  input: FetchMemoryGraphInput = {},
): Promise<MemoryGraphResult> {
  const params = new URLSearchParams();
  if (input.types?.length) params.set('types', input.types.join(','));
  if (input.since) params.set('since', input.since);
  const qs = params.toString();
  const res = await api.get<GraphData>(
    `/api/agent/memory/graph${qs ? `?${qs}` : ''}`,
  );
  const data = unwrap(res);
  const meta = {
    ...DEFAULT_META,
    ...(res.meta as Partial<MemoryGraphMeta> | undefined),
  };
  return {
    nodes: data.nodes ?? [],
    edges: data.edges ?? [],
    layout: data.layout ?? {},
    meta,
  };
}

// ── Recall (semantic search) ────────────────────────────────────────────────

export async function recallMemory(
  query: string,
  k = 8,
): Promise<MemoryRecallResult> {
  return unwrap(
    await api.post<MemoryRecallResult>('/api/agent/memory/recall', { query, k }),
  );
}

// ── Episodes (add_memory) ────────────────────────────────────────────────────

export interface AddMemoryEpisodeInput {
  content: string;
  kind?: string;
  sourceType?: string;
  sourceRefId?: string;
  agentKey?: string;
}

export async function addMemoryEpisode(
  input: AddMemoryEpisodeInput,
): Promise<Episode> {
  return unwrap(await api.post<Episode>('/api/agent/memory/episodes', input));
}

// ── Node lifecycle (soft-delete + restore) ──────────────────────────────────

export interface DeleteMemoryNodeResult {
  id: string;
  deletedAt?: string | null;
  /** Days until the soft-deleted node is purged (server default 30). */
  purgeInDays?: number;
}

export async function deleteMemoryNode(
  nodeId: string,
): Promise<DeleteMemoryNodeResult> {
  return unwrap(
    await api.delete<DeleteMemoryNodeResult>(
      `/api/agent/memory/graph/nodes/${encodeURIComponent(nodeId)}`,
    ),
  );
}

export async function restoreMemoryNode(
  nodeId: string,
): Promise<{ id: string }> {
  return unwrap(
    await api.post<{ id: string }>(
      `/api/agent/memory/graph/nodes/${encodeURIComponent(nodeId)}/restore`,
      {},
    ),
  );
}

// ── Edge lifecycle (cut an AGENT_HAS_ACCESS edge → revoke) ────────────────────

export async function revokeAgentEdge(
  edgeId: string,
): Promise<{ id: string }> {
  return unwrap(
    await api.delete<{ id: string }>(
      `/api/agent/memory/graph/edges/${encodeURIComponent(edgeId)}`,
    ),
  );
}

// ── Rebuild the graph projection ──────────────────────────────────────────────

export async function rebuildMemoryGraph(): Promise<{ ok: boolean }> {
  return unwrap(
    await api.post<{ ok: boolean }>('/api/agent/memory/graph/rebuild', {}),
  );
}

// ── Agent access grants ───────────────────────────────────────────────────────

export async function getMemoryAccess(): Promise<MemoryAccessGrant[]> {
  const res = await api.get<MemoryAccessGrant[]>('/api/agent/memory/access');
  return unwrap(res) ?? [];
}

export async function revokeAgentAccess(
  agentKey: string,
): Promise<MemoryAccessGrant> {
  return unwrap(
    await api.post<MemoryAccessGrant>(
      `/api/agent/memory/access/${encodeURIComponent(agentKey)}/revoke`,
      {},
    ),
  );
}

// ── Export ────────────────────────────────────────────────────────────────────

export async function exportMemory(): Promise<MemoryExport> {
  return unwrap(await api.get<MemoryExport>('/api/agent/memory/export'));
}

// ── Display helpers (node-type ordering + ring grouping) ─────────────────────

/** Server ring assignment per node type (mirrors M5.1 layout). */
export const NODE_TYPE_RING: Record<GraphNodeType, number> = {
  cv: 0,
  skill: 1,
  section: 2,
  experience: 2,
  education: 2,
  certification: 2,
  job: 3,
  application: 3,
  company: 3,
  recruiter: 3,
  agent: 4,
  connector: 4,
  automation: 4,
  episode: 4,
  fact: 4,
  preference: 4,
  knowledge_asset: 4,
};

/**
 * Above this many nodes, a node type collapses into one sized+counted cluster
 * bubble instead of plotting every node individually - shared so the native
 * radial portrait and the web Sigma canvas agree on when a group is "dense".
 */
export const MEMORY_GRAPH_CLUSTER_THRESHOLD = 6;

/** Stable display order for grouped lists (rings, then alpha). */
export const NODE_TYPE_ORDER: GraphNodeType[] = [
  'cv',
  'skill',
  'section',
  'experience',
  'education',
  'certification',
  'job',
  'application',
  'company',
  'recruiter',
  'agent',
  'connector',
  'automation',
  'episode',
  'fact',
  'preference',
  'knowledge_asset',
];

/**
 * Shared colour map for both the native radial portrait and the web SVG
 * portrait, so a node type reads identically on either platform. Soft/muted
 * on purpose - a saturated rainbow (one loud hue per type) is what made the
 * graph read as generic/AI-slop; a single accent (the CV/"you" node) plus a
 * quiet gray-blue gradient that deepens per ring keeps the shape of the graph
 * legible without every dot fighting for attention.
 */
export const NODE_TYPE_COLOR: Record<GraphNodeType, string> = {
  cv: '#5e29ff',
  skill: '#8489a6',
  section: '#8f94ae',
  experience: '#8f94ae',
  education: '#8f94ae',
  certification: '#8f94ae',
  job: '#9da2ba',
  application: '#9da2ba',
  company: '#9da2ba',
  recruiter: '#9da2ba',
  agent: '#acb0c4',
  connector: '#acb0c4',
  automation: '#acb0c4',
  episode: '#acb0c4',
  fact: '#acb0c4',
  preference: '#acb0c4',
  knowledge_asset: '#acb0c4',
};
